import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Database, UserCheck, Eye, Trash2, Download, Mail, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/SEOHead";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogFooter from "@/components/blog/BlogFooter";

const LGPD = () => {
  return (
    <>
      <SEOHead
        title="LGPD - Lei Geral de Proteção de Dados | Magnet Link"
        description="Entenda como o Magnet Link está em conformidade com a LGPD. Conheça seus direitos sobre seus dados pessoais e como exercê-los."
        keywords="LGPD, proteção de dados, dados pessoais, direitos do titular, conformidade"
        canonicalUrl="/lgpd"
      />

      <div className="min-h-screen bg-background">
        <BlogHeader />

        {/* Header */}
        <header className="pt-32 pb-12 md:pt-40 md:pb-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <nav 
              className="mb-8"
              aria-label="Navegação de retorno"
            >
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Voltar ao Início
                </Link>
              </Button>
            </nav>

            <div className="mx-auto max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
                  LGPD - Proteção de Dados
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018)
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <main role="main" aria-label="Informações sobre LGPD">
          <article className="py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-4xl">
                
                {/* O que é LGPD */}
                <section aria-labelledby="oque" className="mb-12">
                  <h2 id="oque" className="text-2xl font-bold mb-4">O que é a LGPD?</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    A Lei Geral de Proteção de Dados Pessoais (LGPD) é a legislação brasileira que 
                    regulamenta o tratamento de dados pessoais por empresas e organizações. 
                    Inspirada no GDPR europeu, ela entrou em vigor em setembro de 2020 e tem 
                    como objetivo proteger os direitos fundamentais de liberdade e privacidade.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    O Magnet Link está em total conformidade com a LGPD, garantindo que seus 
                    dados pessoais sejam tratados de forma segura, transparente e respeitosa.
                  </p>
                </section>

                {/* Seus Direitos - Grid de 6 cards */}
                <section aria-labelledby="direitos" className="mb-12 not-prose">
                  <h2 id="direitos" className="text-2xl font-bold mb-6">Seus Direitos como Titular</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Eye className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Acesso</h3>
                        <p className="text-sm text-muted-foreground">
                          Você pode solicitar uma cópia de todos os dados pessoais que temos sobre você.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <UserCheck className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Correção</h3>
                        <p className="text-sm text-muted-foreground">
                          Solicite a correção de dados incompletos, inexatos ou desatualizados.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Trash2 className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Exclusão</h3>
                        <p className="text-sm text-muted-foreground">
                          Peça a eliminação dos seus dados pessoais tratados com seu consentimento.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Download className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Portabilidade</h3>
                        <p className="text-sm text-muted-foreground">
                          Solicite a transferência dos seus dados para outro serviço ou produto.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Database className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Informação</h3>
                        <p className="text-sm text-muted-foreground">
                          Saiba com quais entidades seus dados foram compartilhados.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <AlertCircle className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Revogação</h3>
                        <p className="text-sm text-muted-foreground">
                          Revogue o consentimento dado para tratamento de dados a qualquer momento.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* Como Tratamos seus Dados */}
                <section aria-labelledby="tratamento" className="mb-12">
                  <h2 id="tratamento" className="text-2xl font-bold mb-4">Como Tratamos seus Dados</h2>
                  
                  <div className="space-y-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="md:w-1/3">
                        <h3 className="font-semibold text-primary">Coleta</h3>
                      </div>
                      <div className="md:w-2/3">
                        <p className="text-muted-foreground">
                          Coletamos apenas os dados necessários para fornecer nossos serviços: 
                          nome, email, e informações que você insere nos formulários. Sempre 
                          com base legal adequada (consentimento ou legítimo interesse).
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="md:w-1/3">
                        <h3 className="font-semibold text-primary">Armazenamento</h3>
                      </div>
                      <div className="md:w-2/3">
                        <p className="text-muted-foreground">
                          Seus dados são armazenados em servidores seguros com criptografia. 
                          Mantemos os dados apenas pelo tempo necessário para cumprir as 
                          finalidades informadas ou por obrigação legal.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="md:w-1/3">
                        <h3 className="font-semibold text-primary">Compartilhamento</h3>
                      </div>
                      <div className="md:w-2/3">
                        <p className="text-muted-foreground">
                          Não vendemos seus dados. Compartilhamos apenas com prestadores de 
                          serviços essenciais (processamento de pagamentos, hospedagem) que 
                          também seguem padrões de proteção de dados.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="md:w-1/3">
                        <h3 className="font-semibold text-primary">Segurança</h3>
                      </div>
                      <div className="md:w-2/3">
                        <p className="text-muted-foreground">
                          Implementamos medidas técnicas e organizacionais: criptografia SSL, 
                          controles de acesso, monitoramento contínuo e treinamento de equipe 
                          sobre proteção de dados.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Bases Legais */}
                <section aria-labelledby="bases" className="mb-12">
                  <h2 id="bases" className="text-2xl font-bold mb-4">Bases Legais que Utilizamos</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Tratamos seus dados pessoais com base nas seguintes hipóteses previstas na LGPD:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    <li>
                      <strong>Consentimento (Art. 7º, I):</strong> Quando você autoriza expressamente, 
                      como ao aceitar cookies ou receber emails de marketing.
                    </li>
                    <li>
                      <strong>Execução de Contrato (Art. 7º, V):</strong> Para prestar os serviços 
                      que você contratou em nossa plataforma.
                    </li>
                    <li>
                      <strong>Legítimo Interesse (Art. 7º, IX):</strong> Para melhorar nossos 
                      serviços e prevenir fraudes, sempre respeitando seus direitos.
                    </li>
                    <li>
                      <strong>Obrigação Legal (Art. 7º, II):</strong> Para cumprir exigências 
                      legais e regulatórias.
                    </li>
                  </ul>
                </section>

                {/* Encarregado de Dados */}
                <section aria-labelledby="dpo" className="mb-12 not-prose">
                  <h2 id="dpo" className="text-2xl font-bold mb-4">Encarregado de Dados (DPO)</h2>
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
                        <span className="font-semibold">Contato do DPO</span>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Nosso Encarregado de Proteção de Dados está disponível para receber 
                        solicitações, reclamações e comunicações relativas ao tratamento de 
                        dados pessoais:
                      </p>
                      <div className="space-y-2 text-muted-foreground">
                        <p><strong>Email:</strong> dpo@magnetlink.com.br</p>
                        <p><strong>Prazo de resposta:</strong> Até 15 dias úteis</p>
                      </div>
                    </CardContent>
                  </Card>
                </section>

                {/* Como Exercer seus Direitos */}
                <section aria-labelledby="exercer" className="mb-12">
                  <h2 id="exercer" className="text-2xl font-bold mb-4">Como Exercer seus Direitos</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Para exercer qualquer um dos seus direitos como titular de dados:
                  </p>
                  <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
                    <li>Envie um email para <strong>dpo@magnetlink.com.br</strong></li>
                    <li>Identifique-se com nome completo e email cadastrado</li>
                    <li>Descreva qual direito deseja exercer</li>
                    <li>Aguarde nossa resposta em até 15 dias úteis</li>
                  </ol>
                  <p className="text-muted-foreground leading-relaxed mt-4">
                    Podemos solicitar informações adicionais para confirmar sua identidade 
                    e garantir a segurança dos seus dados.
                  </p>
                </section>

              </div>
            </div>
          </article>
        </main>

        <BlogFooter />
      </div>
    </>
  );
};

export default LGPD;
